import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
import URL from "../../../../Main/url";
import CurrencyWord from "../../../../Componets/Currencywords";
import { useParams } from "react-router-dom";
import { get_credit_notes_by_invoice } from "../../../../Controllers/Sales/CreditNotes";
import formatCountryName from "../../../../Main/formatCountryName ";
import UserCurrency from "../../../../Componets/UserCurrency";

const A4Invoice = React.forwardRef((props, ref) => {

  let { id } = useParams();

  const { values } = props;

  const headerRef = useRef(null);
  const tableHeaderRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [tableHeaderHeight, setTableHeaderHeight] = useState(0);

  const [creditNotes, setCreditNotes] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  const capitalizeWords = (str) => {
    if (!str) return "";
    return str.replace(/\b\w/g, char => char.toUpperCase());
  };

  const calculateTotalPages = () => {
    if (!values?.details) return 1;
    return Math.ceil(values.details.length / itemsPerPage);
  };

  const getItemsForPage = (page) => {
    if (!values?.details) return [];
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return values.details.slice(startIndex, endIndex);
  };

  // Fetch Credit Notes from V1
  const fetchCreditNotes = async () => {
    if (id) {
      const data = { invoice_id: id };
      const creditNotesResponse = await get_credit_notes_by_invoice(data);
      if (creditNotesResponse?.status) {
        setCreditNotes(creditNotesResponse.data);
      }
    }
  };

  useEffect(() => {
    if (values) {
      fetchCreditNotes();
    }
  }, [values]);

  useEffect(() => {
    if (values?.details) {
      const pages = calculateTotalPages();
      setTotalPages(pages);
    }
  }, [values?.details]);

  useEffect(() => {
    if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    if (tableHeaderRef.current) setTableHeaderHeight(tableHeaderRef.current.offsetHeight);
  }, [values]);

  const injectScopedStyles = (headerH, tableHeaderH) => {

    const existing = document.getElementById("a4-invoice-styles");
    if (existing) existing.remove();

    const style = document.createElement("style");
    style.id = "a4-invoice-styles";

    const contentMargin = headerH + tableHeaderH;

    style.innerHTML = `
      @page {
        size: A4;
        margin: 3% 6% 5% 6%;
        @bottom-left {
          content: "Printed on: ${moment(new Date().toISOString()).format("DD-MM-YYYY")}";
          font-size: 10px;
        }
        @bottom-right {
          content: "Page " counter(page) " of " counter(pages);
          font-size: 10px;
        } 
      }
  
      @media print {
        .header-section {
          position: fixed;
          top: 0%;
          left: 0%;
          right: 0%;
          background: white;
          z-index: 1000;
          page-break-inside: avoid;
        }
        
        .table-header-section {
          position: fixed;
          top: ${headerH}px;  
          left: 0%;
          right: 0%;
          background: white;
          z-index: 999;
          page-break-inside: avoid;
        }
        
        .invoice-content {
          margin-top: ${contentMargin}px;
        }
        
        .page-break {
          page-break-before: always;
          margin-top: ${contentMargin}px;  
        }
        
        .a4-invoice-footer {
          font-size: 12px;
          white-space: pre;
          position: fixed;
          bottom: 10mm;
          left: 6%;
        }
  
        .a4-invoice-footer-right {
          font-size: 12px;
          white-space: pre;
          position: fixed;
          bottom: 10mm;
          right: 6%;
        }
      }

      .invoice-table {
        table-layout: fixed;
        width: 100%;
      }

      /* Column fixed widths matched from V2 layout */
      .col-sno    { width: 5%; }
.col-desc   { width: 38%; }
.col-unit   { width: 8%; }
.col-qty    { width: 8%; }
.col-ws     { width: 12%; } /* Rate */
.col-disc   { width: 10%; }
.col-foc    { width: 6%; }
.col-vat    { width: 6%; }
.col-total  { width: 7%; }
    `;
    document.head.appendChild(style);
  };
  useEffect(() => {
    if (values?.branch?.name && headerHeight && tableHeaderHeight) {
      injectScopedStyles(headerHeight, tableHeaderHeight);
    }
  }, [values?.branch?.name, headerHeight, tableHeaderHeight]);

  const renderHeaderSection = () => (
    <div className="header-section" ref={headerRef}>
      <div className="flex justify-between items-start">
        <div className="text-black-900 text-[0.65rem]">
          <div className="font-semibold text-md">{capitalizeWords(values?.branch?.name)}</div>
          <div className="text-xs">
            Email : {values?.branch?.email}
          </div>
          <div className="text-xs">
            Phone : {values?.branch?.phone}
          </div>
          <div className="text-xs">
            {values?.branch?.area ? values?.branch?.area + ", " : ""}
            {values?.branch?.city ? values?.branch?.city + ", " : ""}
            {formatCountryName(values?.branch?.country)}
          </div>
          <div className="text-xs">
            VATIN : {values?.branch?.tax || "N/A"}
          </div>
        </div>
        {values?.branch?.image ? (
          <div className="flex flex-col items-center">
            <img
              className="w-[50px] h-[50px]"
              src={`${URL}/uploads${values?.branch?.image}`}
              alt="Branch Logo"
            />
            <div className="font-semibold text-lg">{capitalizeWords(values?.branch?.name)}</div>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="w-[50px] h-[50px] rounded-md bg-gray-300 text-black flex items-center justify-center text-xl font-semibold">
              {values?.branch?.name ? values.branch.name.charAt(0).toUpperCase() : "N"}
            </div>
            <div className="font-semibold text-lg">{capitalizeWords(values?.branch?.name)}</div>
          </div>
        )}
      </div>

      <hr className="my-2 border-black" />
      <div className="text-center text-md py-1 font-semibold">TAX INVOICE</div>
      <hr className="my-2 border-black" />

      <div className="border border-gray-300 rounded-lg p-4 mb-1 shadow-sm">
        <div className="flex justify-between">
          <div>
            <div className="font-semibold text-[0.70rem] mb-2">Customer Name</div>
            <div className="text-[0.65rem] space-y-1">
              <div className="font-medium">
                {values?.customer?.name || "Walk-in Customer"}
              </div>

              {values?.customer?.phone && (
                <div>
                  <span className="font-semibold">Phone :</span> {values?.customer?.phone}
                </div>
              )}

              {values?.customer?.email && (
                <div>
                  <span className="font-semibold">Email :</span> {values?.customer?.email}
                </div>
              )}

              <div>
                {values?.customer?.city ? values?.customer?.city + ", " : ""}{values?.customer?.state}
              </div>

              <div>{formatCountryName(values?.customer?.country)}</div>

              {values?.customer?.tax && (
                <div>
                  <span className="font-semibold">VATIN :</span> {values?.customer?.tax}
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="text-[0.65rem] space-y-1">
              <div className="flex">
                <div className="w-24 font-bold">Invoice No</div>
                <div className="px-2">:</div>
                <div className="text-right">#INV-{values?.number}</div>
              </div>
              <div className="flex">
                <div className="w-24 font-bold">Invoice Type</div>
                <div className="px-2">:</div>
                <div className="text-right">
                  {values?.payment_status == 2
                    ? values?.payments
                      ?.map((v, i) => (i > 0 ? ", " : "") + v?.name)
                      .join("")
                    : "CREDIT"}
                </div>
              </div>
              <div className="flex">
                <div className="w-24 font-bold">Ref</div>
                <div className="px-2">:</div>
                <div className="text-right">
                  {values?.converted_from_booking
                    ? `Booking #${values?.converted_from_booking?.number}`
                    : values?.delivery_note
                      ? `DN #${values?.delivery_note?.number}`
                      : "Direct"}
                </div>
              </div>
              <div className="flex">
                <div className="w-24 font-bold">Date</div>
                <div className="px-2">:</div>
                <div className="text-right">
                  {moment(values?.date).format("DD-MM-YYYY")}
                </div>
              </div>
              {values?.due_date && (
                <div className="flex">
                  <div className="w-24 font-bold">Due Date</div>
                  <div className="px-2">:</div>
                  <div className="text-right">
                    {moment(values?.due_date).format("DD-MM-YYYY")}
                  </div>
                </div>
              )}
              {values?.lpo_number && (
                <div className="flex">
                  <div className="w-24 font-bold">LPO No</div>
                  <div className="px-2">:</div>
                  <div className="text-right">{values?.lpo_number}</div>
                </div>
              )}
              {values?.lpo_date && (
                <div className="flex">
                  <div className="w-24 font-bold">LPO Date</div>
                  <div className="px-2">:</div>
                  <div className="text-right">
                    {moment(values?.lpo_date).format("DD-MM-YYYY")}
                  </div>
                </div>
              )}
              {values?.salesman?.name && (
                <div className="flex">
                  <div className="w-24 font-bold">Salesman</div>
                  <div className="px-2">:</div>
                  <div className="text-right">{values?.salesman?.name}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  const renderTableHeaders = () => (
    <div className="table-header-section pt-[8px]" ref={tableHeaderRef}>
      <table className="invoice-table w-full border-separate border-spacing-0 rounded-t-lg overflow-hidden border border-gray-300 mb-0">
        <thead>
          <tr className="bg-gray-100 border-b border-gray-300">
            <th className="col-sno text-[0.60rem] text-left py-2 pl-3 border-b border-gray-300 rounded-tl-lg">
              <div className="text-[0.60rem]">S.NO</div>
            </th>
            <th className="col-desc text-[0.60rem] text-left p-1 border-b border-gray-300">
              <div className="text-[0.60rem]">DESCRIPTION</div>
            </th>
            {/* <th className="col-batch text-[0.60rem] text-left p-1 border-b border-gray-300">
              BATCH / EXPIRY
            </th> */}
            <th className="col-unit text-[0.60rem] text-left p-1 border-b border-gray-300">
              <div className="text-[0.60rem]">UNIT</div>
            </th>
            <th className="col-qty text-[0.60rem] text-left p-1 border-b border-gray-300">
              <div className="text-[0.60rem]">QTY</div>
            </th>
            <th className="col-ws text-[0.60rem] text-left p-1 border-b border-gray-300">
              <div className="text-[0.60rem]">PRICE</div>
            </th>
            <th className="col-disc text-[0.60rem] text-left p-1 border-b border-gray-300">
              <div className="text-[0.60rem]">DISC</div>
            </th>
            <th className="col-foc text-[0.60rem] text-left p-1 border-b border-gray-300">
              <div className="text-[0.60rem]">FOC</div>
            </th>
            <th className="col-vat text-[0.60rem] text-left p-1 border-b border-gray-300">
              <div className="text-[0.60rem]">VAT</div>
            </th>
            <th className="col-total text-[0.60rem] text-right p-1 border-b border-gray-300 rounded-tr-lg">
              <div className="text-[0.60rem]">TOTAL</div>
            </th>
          </tr>
        </thead>
      </table>
    </div>
  );
  const renderTableBody = (pageItems, pageNumber, isLastPage) => (
    <div key={`page-${pageNumber}`} className={pageNumber > 1 ? "page-break" : "invoice-content"}>
      <table className="invoice-table w-full border-separate border-spacing-0 overflow-hidden border border-gray-300 border-t-0">
        <tbody className="text-sm">
          {pageItems.map((value, index) => {
            const isLastDataRow = index === pageItems.length - 1;
            const globalIndex = (pageNumber - 1) * itemsPerPage + index;
            const isLastOverallRow = isLastPage && isLastDataRow;

            return (
              <tr key={`${pageNumber}-${index}`} className="hover:bg-gray-50">
                <td className={`col-sno text-[0.60rem] text-left p-1 pl-3 ${isLastOverallRow ? "rounded-bl-lg" : ""}`}>
                  {globalIndex + 1}
                </td>
                <td className="col-desc text-[0.60rem]">
                  {value?.name}
                </td>
                {/* <td className="col-batch text-[0.60rem] text-left p-1">
                  <div className="text-[0.60rem]">
                    #BATCH-{value?.description?.number}
                  </div>
                  <div className="text-[0.60rem] text-left text-gray-500">
                    {value?.expiry_date
                      ? `Expiry : ${moment(value?.expiry_date).format("DD-MM-YYYY")}`
                      : ""}
                  </div>
                </td> */}
                <td className="col-unit text-[0.60rem] text-left p-1">
                  {value?.unit_name}
                </td>
                <td className="col-qty text-[0.60rem] text-left p-1">
                  {value?.quantity}
                </td>
                <td className="col-price text-[0.60rem] text-left p-1">
                  {value?.description?.sale_price?.toFixed(3)}
                </td>
                <td className="col-ws text-[0.60rem] text-left p-1">
                  {value?.sale_price?.toFixed(3)}
                </td>
                <td className="col-disc text-[0.60rem] text-left p-1">
                  <div className="text-[0.60rem]">
                    {value?.discount_percentage}%
                  </div>
                  <div className="text-[0.60rem] text-gray-500 leading-[0.75rem] align-top">
                    {value?.discount_price?.toFixed?.(3)}
                  </div>
                </td>
                <td className="col-foc text-[0.60rem] text-left p-1">
                  {value?.free ? value?.free : 0}
                </td>
                <td className="col-vat text-[0.60rem] text-left p-1">
                  <div className="text-[0.60rem] ">{value?.tax}%</div>
                  <div className="text-[0.60rem] text-gray-500">
                    {value?.tax_amount?.toFixed(3)}
                  </div>
                </td>
                <td className="col-total text-[0.60rem] text-right p-1">
                  {value?.total?.toFixed(3)}
                </td>
              </tr>
            );
          })}

          {pageItems.length < itemsPerPage && Array.from({
            length: itemsPerPage - pageItems.length,
          }).map((_, index, arr) => {
            const isLastEmptyRow = index === arr.length - 1;
            const lastRowRounded = isLastPage && isLastEmptyRow;

            {/* <tfoot className="text-sm ">
                <tr className="">
                  <th colSpan={8}></th>
                  <th className="font-normal pt-1 text-right p-1 text-[0.60rem]">
                    Subtotal
                  </th>
                  <td className="font-normal text-right p-1 text-[0.60rem]">
                    {values?.subtotal?.toFixed(3)}
                  </td>
                </tr>
                <tr>
                  <th colSpan={8}></th>
                  <th className="font-normal text-right p-1 text-[0.60rem]">
                    VAT (5%)
                  </th>
                  <td className="font-normal text-right p-1 text-[0.60rem]">
                    {values?.taxamount?.toFixed(3)}
                  </td>
                </tr>
                <tr>
                  <th colSpan={8}></th>
                  <th className="font-normal text-right p-1 text-[0.60rem]">
                    Discount
                  </th>
                  <td className="font-normal text-right p-1 text-[0.60rem]">
                    {values?.discount_type
                      ? values?.discount_value
                        ? values?.discount?.toFixed(3) +
                          " " +
                          "(-" +
                          values?.discount_value +
                          "%)"
                        : "0.000"
                      : values?.discount
                      ? "-" + values?.discount?.toFixed(3)
                      : "0.000"}
                  </td>
                </tr>
                {values?.delivery ? (
                  <tr>
                    <th colSpan={8}></th>
                    <th className="font-normal text-right p-1 text-[0.60rem]">
                      Delivery Charge
                    </th>
                    <td className="font-normal text-right p-1 text-[0.60rem]">
                      {values?.delivery
                        ? "+" + values?.delivery?.toFixed(3)
                        : "0.000"}
                    </td>
                  </tr>
                ) : (
                  ""
                )}
                <tr>
                  <th className="" colSpan={8}></th>
                  <th className="font-normal text-right text-white bg-black p-2 text-[0.60rem]">
                    Grand&nbsp;Total&nbsp;(OMR)
                  </th>
                  <td className="font-normal text-right text-white bg-black p-2 text-[0.60rem]">
                    {values?.total?.toFixed(3)}
                  </td>
                </tr>
              </tfoot> */}
            return (
              <tr key={`empty-${pageNumber}-${index}`} className="hover:bg-gray-50">
                <td className={`text-[0.60rem] p-2 pl-3 ${lastRowRounded ? "rounded-bl-lg" : ""}`}>
                  &nbsp;
                </td>
                <td className="p-2">&nbsp;</td>
                <td className="p-2">&nbsp;</td>
                <td className="p-2">&nbsp;</td>
                <td className="p-2">&nbsp;</td>
                <td className="p-2">&nbsp;</td>
                <td className="p-2">&nbsp;</td>
                <td className="p-2">&nbsp;</td>
                {/* <td className="p-2">&nbsp;</td> */}
                <td className={`text-right text-[0.60rem] p-1 pr-2 ${lastRowRounded ? "rounded-br-lg" : ""}`}>
                  &nbsp;
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {isLastPage && (
        <div className="border border-gray-300 rounded-lg p-4 mb-2 mt-2 shadow-sm">
          <div className="flex justify-between">
            <div className="text-[0.65rem] w-1/2">
              {(values?.branch?.bank_name || values?.branch?.bank_swift) && (
                <div className="mt-3 space-y-1">
                  {values?.branch?.bank_name && (
                    <div className="flex gap-2">
                      <span className="font-semibold w-24">Bank Name</span>
                      <span>: {values?.branch?.bank_name}</span>
                    </div>
                  )}
                  {values?.branch?.bank_branch && (
                    <div className="flex gap-2">
                      <span className="font-semibold w-24">Branch</span>
                      <span>: {values?.branch?.bank_branch}</span>
                    </div>
                  )}
                  {values?.branch?.bank_account_name && (
                    <div className="flex gap-2">
                      <span className="font-semibold w-24">Account Name</span>
                      <span>: {values?.branch?.bank_account_name}</span>
                    </div>
                  )}
                  {values?.branch?.bank_account_number && (
                    <div className="flex gap-2">
                      <span className="font-semibold w-24">Account No</span>
                      <span>: {values?.branch?.bank_account_number}</span>
                    </div>
                  )}
                  {values?.branch?.bank_swift && (
                    <div className="flex gap-2">
                      <span className="font-semibold w-24">Swift Code</span>
                      <span>: {values?.branch?.bank_swift}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="text-[0.65rem] w-1/2">
              <table className="w-full text-[0.65rem]">
                <tbody>
                  <tr>
                    <td className="text-right pr-2 py-1">Subtotal</td>
                    <td className="text-right w-20 py-1">{values?.subtotal?.toFixed(3)}</td>
                  </tr>
                  <tr>
                    <td className="text-right pr-2 py-1">VAT (5%)</td>
                    <td className="text-right py-1">{values?.taxamount?.toFixed(3)}</td>
                  </tr>
                  <tr>
                    <td className="text-right pr-2 py-1">Discount</td>
                    <td className="text-right py-1">
                      {values?.discount_type
                        ? values?.discount_value
                          ? `${values?.discount?.toFixed(3)} (-${values?.discount_value}%)`
                          : "0.000"
                        : values?.discount
                          ? `-${values?.discount?.toFixed(3)}`
                          : "0.000"}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right pr-2 py-1">Delivery Charges</td>
                    <td className="text-right py-1">
                      {values?.delivery?.toFixed(3) || "0.000"}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right pr-2 py-2 font-semibold">Total (<UserCurrency />)</td>
                    <td className="text-right py-2 font-semibold">{values?.total?.toFixed(3)}</td>
                  </tr>
                  <tr>
                    <td
                      colSpan={2}
                      className="border-t pt-2 text-right"
                    >
                      <span className="font-semibold">Total in words: </span>
                      <CurrencyWord amount={values?.total?.toFixed(3)} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )
      }

      {/* Credit Notes Section from V1 (Rendered on final page layout) */}
      {isLastPage && (
        <div className="mt-4">
          <div className="border border-gray-300 rounded-lg p-4 shadow-sm">
            <h3 className="text-[0.75rem] font-semibold mb-3 uppercase">Credit Notes</h3>
            {creditNotes.length > 0 ? (
              <table className="w-full border-separate border-spacing-0 border border-gray-300 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-gray-100 border-b border-gray-300 text-[0.65rem] uppercase">
                    <th className="p-2 text-left">S.NO</th>
                    <th className="p-2 text-left">DESCRIPTION</th>
                    <th className="p-2 text-center">UNIT</th>
                    <th className="p-2 text-center">QTY</th>
                    <th className="p-2 text-center">PRICE</th>
                    <th className="p-2 text-center">DISC</th>
                    <th className="p-2 text-center">TAX</th>
                    <th className="p-2 text-right">TOTAL</th>
                  </tr>
                </thead>
                <tbody className="text-[0.60rem]">
                  {creditNotes.map((note, noteIndex) =>
                    note.details.map((item, index) => (
                      <tr key={item._id || `${noteIndex}-${index}`} className="border-b border-gray-300 uppercase">
                        <td className="p-2">{index + 1}</td>
                        <td className="p-2">
                          {item.name}
                          {item.description?.number && (
                            <div className="text-gray-500 text-[0.55rem]">
                              #BATCH-{item.description.number}
                            </div>
                          )}
                        </td>
                        <td className="p-2 text-center">{item.unit_name}</td>
                        <td className="p-2 text-center">{item.quantity}</td>
                        <td className="p-2 text-center">{item.price_per_unit?.toFixed(3)}</td>
                        <td className="p-2 text-center">
                          {item.discount_percentage}%<br />
                          <span className="text-gray-500">{item.discount_price?.toFixed(3)}</span>
                        </td>
                        <td className="p-2 text-center">
                          {item.tax?.rate}%<br />
                          <span className="text-gray-500">{item.tax_amount?.toFixed(3)}</span>
                        </td>
                        <td className="p-2 text-right">{item.total?.toFixed(3)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : (
              <p className="text-gray-500 text-[0.65rem]">No credit notes found for this invoice.</p>
            )}
          </div>
        </div>
      )}

      {/* Note Description Section from V1 */}
      {isLastPage && values?.note && (
        <div
          className="pt-4 text-regular text-sm"
          dangerouslySetInnerHTML={{ __html: values?.note }}
        />
      )}

      {isLastPage && (
        <div className="flex justify-between pt-10 text-[0.60rem]">
          <div className="text-center">
            <div className="border-b border-gray-400 w-32 mb-1"></div>
            <div className="font-semibold">Received By</div>
          </div>
          <div className="text-center">
            <div className="border-b border-gray-400 w-32 mb-1"></div>
            <div className="font-semibold">Checked By</div>
          </div>
          <div className="text-center">
            <div className="border-b border-gray-400 w-32 mb-1"></div>
            <div className="font-semibold">Prepared By</div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="a4-invoice" ref={ref}>
      <style>{`
        @media print {
          .a4-invoice { page-break-before: always; }
          @page { size: A4; margin: 3% 6% 5% 6%; }
        }
        @media screen {
          .header-section       { position: static; }
          .table-header-section { position: static; }
          .invoice-content      { margin-top: 0; }
          .page-break           { margin-top: 0; page-break-before: auto; }
        }
      `}</style>

      {renderHeaderSection()}

      {renderTableHeaders()}

      {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => {
        const pageItems = getItemsForPage(pageNumber);
        const isLastPage = pageNumber === totalPages;
        return renderTableBody(pageItems, pageNumber, isLastPage);
      })}
    </div>
  );
});

export default A4Invoice;